import React, { useMemo, useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import { Box, CircularProgress, InputAdornment, MenuItem, Popover, TextField } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import { getDispatchRestaurantDrivers, getDispatchRidersByZone } from '../../apollo'
import { C, tnum } from './tokens'
import { canSendToFirstDelivery, canSendToRushmyfood, haversineMiles } from './model'
import { PersonAvatar } from './Badges'
import { useDispatchUi } from './context'

const GET_RIDERS = gql`${getDispatchRidersByZone}`
const GET_OWN_DRIVERS = gql`${getDispatchRestaurantDrivers}`

const coords = c => (Array.isArray(c) && c.length >= 2 && c[0] != null && c[1] != null ? { lat: +c[1], lng: +c[0] } : null)

function Heading({ children }) {
  return (
    <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: C.muted }}>
      {children}
    </Box>
  )
}

/**
 * Rider picker for one or many orders. `children(open)` renders the trigger.
 * Riders come from the first order's zone, as the old picker did — or, on a
 * restaurant's own board, that restaurant's drivers. Offline riders are listed
 * but can't be picked.
 */
export default function AssignDriverMenu({ views, children, anchorOrigin, transformOrigin }) {
  const ui = useDispatchUi()
  const [anchor, setAnchor] = useState(null)
  const [q, setQ] = useState('')
  const first = views[0]
  const zoneId = first?.restaurant.zoneId
  const mixedZones = new Set(views.map(v => v.restaurant.zoneId)).size > 1
  const restaurantIds = new Set(views.map(v => v.restaurant.id))
  const onlyRestaurant = restaurantIds.size === 1 ? first?.restaurant : null
  const origin = first?.restaurant.location
  const ownDrivers = !!ui.restaurantId

  const zoneRiders = useQuery(GET_RIDERS, {
    variables: { id: zoneId },
    skip: ownDrivers || !anchor || !zoneId,
    fetchPolicy: 'cache-and-network',
  })
  const restaurantDrivers = useQuery(GET_OWN_DRIVERS, {
    variables: { restaurantId: ui.restaurantId },
    skip: !ownDrivers || !anchor,
    fetchPolicy: 'cache-and-network',
  })
  const { data, loading } = ownDrivers ? restaurantDrivers : zoneRiders

  const riders = useMemo(() => {
    const list = ((ownDrivers ? data?.restaurantDrivers : data?.ridersByZone) || []).map(r => {
      const own = r.driverType === 'RESTAURANT' && onlyRestaurant && String(r.restaurantId) === String(onlyRestaurant.id)
      const loc = coords(r.location?.coordinates)
      return {
        ...r,
        eligible: !!(r.isActive && r.available),
        busy: ui.busyDriverIds.has(String(r._id)),
        miles: origin && loc ? haversineMiles(origin, loc) : null,
        kindLabel: r.driverType === 'RESTAURANT' ? (own ? 'Staff · this restaurant' : 'Staff · other restaurant') : 'RushMyFood',
        rank: r.driverType === 'RESTAURANT' && !own ? 1 : 0,
      }
    })
    const needle = q.trim().toLowerCase()
    return list
      .filter(r => !needle || String(r.name || '').toLowerCase().includes(needle))
      .sort((a, b) =>
        Number(b.eligible) - Number(a.eligible) ||
        a.rank - b.rank ||
        Number(a.busy) - Number(b.busy) ||
        (a.miles ?? 999) - (b.miles ?? 999))
  }, [data, ownDrivers, q, onlyRestaurant, origin, ui.busyDriverIds])

  const available = riders.filter(r => r.eligible)
  const offline = riders.filter(r => !r.eligible)
  const fdTargets = ui.firstDelivery ? views.filter(canSendToFirstDelivery) : []
  const rmfTargets = ui.rushmyfood ? views.filter(canSendToRushmyfood) : []
  const close = () => {
    setAnchor(null)
    setQ('')
  }

  return (
    <>
      {children(e => {
        e.stopPropagation()
        setAnchor(e.currentTarget)
      })}
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        onClick={e => e.stopPropagation()}
        anchorOrigin={anchorOrigin || { vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={transformOrigin || { vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 320, mt: 0.5, borderRadius: '10px', border: `1px solid ${C.border}`, boxShadow: '0 12px 32px -8px rgba(16,24,40,0.2)' } }}
      >
        <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.muted }}>
          {views.length === 1 ? (
            <>
              Assign <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, color: C.text }}>#{first.orderNo}</Box>
              {onlyRestaurant && <> · from {onlyRestaurant.name}</>}
            </>
          ) : (
            <>Assign one driver to {views.length} orders</>
          )}
          {mixedZones && !ownDrivers && <Box sx={{ mt: 0.5, color: C.faint }}>{"Orders span zones — showing the first order's zone."}</Box>}
        </Box>
        <Box sx={{ p: 1, borderBottom: `1px solid ${C.border}` }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="Search drivers…"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.stopPropagation()}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: C.muted }} /></InputAdornment>,
              sx: { fontSize: 13, borderRadius: '8px', height: 34 },
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 300, overflowY: 'auto', pb: 0.5 }}>
          {!ownDrivers && !zoneId ? (
            <Box sx={{ px: 1.5, py: 2, fontSize: 13, color: C.muted }}>This restaurant has no zone, so there are no riders to offer.</Box>
          ) : loading && !data ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={18} /></Box>
          ) : riders.length === 0 ? (
            <Box sx={{ px: 1.5, py: 2, fontSize: 13, color: C.muted }}>{q ? 'No driver by that name.' : ownDrivers ? 'This restaurant has no drivers of its own yet.' : 'No riders in this zone.'}</Box>
          ) : (
            <>
              {available.length > 0 && <Heading>Available</Heading>}
              {available.map(r => (
                <MenuItem
                  key={r._id}
                  onClick={() => {
                    close()
                    ui.assign(views, r)
                  }}
                  sx={{ gap: 1.25, py: 0.75, mx: 0.5, borderRadius: '6px' }}
                >
                  <PersonAvatar name={r.name} size={26} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontSize: 13, fontWeight: 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</Box>
                    <Box sx={{ fontSize: 12, color: C.muted }}>{r.kindLabel}</Box>
                  </Box>
                  <Box sx={{ textAlign: 'right', fontSize: 12, ...tnum }}>
                    {r.miles != null && <Box sx={{ color: C.text }}>{r.miles.toFixed(1)} mi</Box>}
                    <Box sx={{ color: r.busy ? '#0e7490' : '#067647' }}>{r.busy ? 'On a run' : 'Free'}</Box>
                  </Box>
                </MenuItem>
              ))}
              {offline.length > 0 && <Heading>Offline</Heading>}
              {offline.map(r => (
                <MenuItem key={r._id} disabled sx={{ gap: 1.25, py: 0.75, mx: 0.5, borderRadius: '6px' }}>
                  <PersonAvatar name={r.name} size={26} />
                  <Box sx={{ flex: 1, fontSize: 13, color: C.text }}>{r.name}</Box>
                  <Box sx={{ fontSize: 12, color: C.muted }}>Offline</Box>
                </MenuItem>
              ))}
            </>
          )}
          {fdTargets.length > 0 && (
            <>
              <Box sx={{ my: 0.5, borderTop: `1px solid ${C.border}` }} />
              <Heading>Courier network</Heading>
              <MenuItem
                onClick={() => {
                  close()
                  ui.requestFirstDelivery(fdTargets)
                }}
                sx={{ gap: 1.25, py: 0.75, mx: 0.5, borderRadius: '6px' }}
              >
                <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: C.ink, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LocalShippingIcon sx={{ fontSize: 15 }} />
                </Box>
                <Box>
                  <Box sx={{ fontSize: 13, fontWeight: 500, color: C.text }}>Send to First Delivery</Box>
                  <Box sx={{ fontSize: 12, color: C.muted }}>
                    {fdTargets.length < views.length ? `${fdTargets.length} of ${views.length} can go` : 'Hand off to their courier network now'}
                  </Box>
                </Box>
              </MenuItem>
            </>
          )}
          {rmfTargets.length > 0 && (
            <>
              <Box sx={{ my: 0.5, borderTop: `1px solid ${C.border}` }} />
              <Heading>RushMyFood</Heading>
              <MenuItem
                onClick={() => {
                  close()
                  ui.requestRushmyfood(rmfTargets)
                }}
                sx={{ gap: 1.25, py: 0.75, mx: 0.5, borderRadius: '6px' }}
              >
                <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: C.ink, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LocalShippingIcon sx={{ fontSize: 15 }} />
                </Box>
                <Box>
                  <Box sx={{ fontSize: 13, fontWeight: 500, color: C.text }}>Send to RushMyFood Delivery</Box>
                  <Box sx={{ fontSize: 12, color: C.muted }}>
                    {rmfTargets.length < views.length ? `${rmfTargets.length} of ${views.length} can go` : 'A RushMyFood driver delivers it'}
                  </Box>
                </Box>
              </MenuItem>
            </>
          )}
        </Box>
      </Popover>
    </>
  )
}
